import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  PLATFORM_ID,
  isDevMode,
  inject,
  provideAppInitializer
} from '@angular/core';
import {
  provideRouter,
  Router,
  withDebugTracing,
  withEnabledBlockingInitialNavigation,
  withRouterConfig
} from '@angular/router';
import { isPlatformServer } from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors
} from '@angular/common/http';
import { MatNativeDateModule } from '@angular/material/core';

import * as Sentry from '@sentry/angular';

import { routes } from '@/app.routes';
import { AppService } from '@/core/app.service';
import { provideServiceWorker } from '@angular/service-worker';
import { UI_OPTIONS } from '@factor_ec/ui';
import { languageInterceptor } from './core/language.interceptor';

import { environment } from '../environments/environment';

import { provideAuth0, authHttpInterceptorFn } from '@auth0/auth0-angular';
import config from '../../capacitor.config';

/**
 * @description Proporciona los proveedores de Sentry según la plataforma
 * @param {object} platformId - ID de la plataforma para determinar si es servidor
 * @returns {unknown[]} Array de proveedores de Sentry
 */
function provideSentryProviders(platformId: object): unknown[] {
  if (isPlatformServer(platformId)) {
    return [];
  }
  return [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        logErrors: true,
        showDialog: true
      })
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    }
  ];
}

const isWindowAvailable = typeof window !== 'undefined';
const isNativePlatform =
  isWindowAvailable &&
  !!(globalThis as any).Capacitor &&
  typeof (globalThis as any).Capacitor.isNativePlatform === 'function'
    ? (globalThis as any).Capacitor.isNativePlatform()
    : false;

export const callbackUri = isNativePlatform
  ? `${config.appId}://${environment.auth0.domain}/callback`
  : environment.auth0.authorizationParams?.redirect_uri ||
    'http://localhost:4200/callback';
console.log('Callback URI:', callbackUri);
/**
 * @description Configuración principal de la aplicación Angular
 * @constant
 * @type {ApplicationConfig}
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),

    // Inicializador de la aplicación
    provideAppInitializer(async () => {
      // Dependency injection
      const appService = inject(AppService);
      const sessionInitialized = await appService.initialize();
      if (!sessionInitialized) {
        return new Promise(() => {});
      }
      return true;
    }),

    // Cliente HTTP con interceptores
    provideHttpClient(
      withFetch(),
      withInterceptors([authHttpInterceptorFn, languageInterceptor])
    ),

    // Configuración de Sentry
    {
      provide: 'sentryProviders',
      useFactory: provideSentryProviders,
      deps: [PLATFORM_ID]
    },

    // Módulos adicionales
    importProvidersFrom(MatNativeDateModule),

    // Service Worker
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),

    // Configuración de UI
    {
      provide: UI_OPTIONS,
      useValue: {
        iconSettings: {
          path: 'images',
          collection: 'factoricons-regular'
        }
      }
    },

    // Auth0 provider
    provideAuth0({
      ...environment.auth0,
      authorizationParams: {
        ...environment.auth0?.authorizationParams,
        redirect_uri: callbackUri,
        scope: 'openid profile email offline_access'
      },
      cacheLocation: 'localstorage'
    })
  ]
};
